/* @flow */
import cx from 'classnames';
import PropTypes from 'prop-types';
import * as React from 'react';

import Button from 'components/Button';
import Buttons from 'components/Buttons';
import ComposerAlert from 'chat/components/ComposerAlert';
import Glyph from 'components/Glyph';
import MediaObject from 'components/MediaObject';
import CurrentUser from 'auth/CurrentUser';
import ModalTrigger from 'components/ModalTrigger';
import { subgroupName } from 'utils/roleHelpers';
import { SplitComposerModal } from 'chat/components/SplitComposer';
import { SplitComposerModal as NewComposerModal } from 'chat/components/FullPageComposer/SplitComposer';
import { clearDraftAnnouncement } from 'groups/actions/DraftMessageActions';

import type { Target, GroupTarget, OrganizationTarget } from 'entities/Message';

import './styles/AnnouncementComposerButton.scss';

type Props = {
  onMessageSent: (targets: Target[]) => void,
  target: GroupTarget | OrganizationTarget,
  threadUuid?: ?string,
  className?: string,
};

export default class AnnouncementComposerButton extends React.Component<Props> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  getPlaceholder = () => {
    const { target } = this.props;
    const groupOrOrgName = target && target.name;
    const role = target.type === 'group_target' && target && target.role;

    if (groupOrOrgName && role) {
      return `Message ${subgroupName[role].toLowerCase()} in ${groupOrOrgName}`;
    } else if (groupOrOrgName) {
      return `Message ${groupOrOrgName}`;
    } else {
      return 'Type a message';
    }
  };

  hasNewComposer = () => {
    const currentUser = CurrentUser.getUser();
    return currentUser && currentUser.hasFeature('full_page_composer.web.oct-20');
  };

  getComposerModal() {
    return this.hasNewComposer() ? NewComposerModal : SplitComposerModal;
  }

  renderComposerAlert() {
    const { target, threadUuid } = this.props;
    // TODO: modify ComposerAlert to take org_id (@topherauyeung)
    if (target.type === 'group_target' && target.group_id) {
      return <ComposerAlert threadUuid={threadUuid} groupId={target.group_id} target={target} />;
    }
    return null;
  }

  renderComposerArea() {
    const { target } = this.props;
    if (!CurrentUser.getUser().canComposeAnnouncements()) {
      return null;
    }
    const ComposerModal = this.getComposerModal();
    return (
      <ModalTrigger
        data-test-selector="unified-composer-trigger"
        modal={<ComposerModal initialTargets={[target]} skipSelectRecipients />}>
        {this.renderUnifiedComposerButton()}
      </ModalTrigger>
    );
  }

  renderUnifiedComposerButton() {
    return (
      <div
        className="AnnouncementComposerButton-composer"
        onClick={() => {
          clearDraftAnnouncement(); // Clear between group changes
        }}>
        <MediaObject
          figure="right"
          gutter="xxsmall"
          className="AnnouncementComposerButton-composer-inner">
          <div className="AnnouncementComposerButton-composer-input">
            <MediaObject figure="right" className="AnnouncementComposerButton-composer-input-inner">
              <div className="AnnouncementComposerButton-composer-input-text">
                {this.getPlaceholder()}
              </div>
              <Buttons reset="none" className="AnnouncementComposerButton-composer-buttons">
                <Glyph name="camera-fill" size="large" color="brand" />
                <Glyph name="paperclip" size="large" color="brand" />
              </Buttons>
            </MediaObject>
          </div>
          <Button disabled size="large">
            Send
          </Button>
        </MediaObject>
      </div>
    );
  }

  render() {
    return (
      <div className={cx('AnnouncementComposerButton', this.props.className)}>
        {this.renderComposerAlert()}
        {this.renderComposerArea()}
      </div>
    );
  }
}
